import React from "react";

import { CancelRounded, CheckCircleRounded, PendingActionsRounded } from "@mui/icons-material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { Box, ButtonBase, Divider, Skeleton } from "@mui/material";
import { DateTime } from "luxon";
import { useNavigate, useParams } from "react-router-dom";

import useModal from "../../../config/context/modalContext";
import { AuthService } from "../../../config/services/AuthService";
import { ReportService } from "../../../config/services/ReportService";
import { SpottService } from "../../../config/services/SpottService";
import { GreyPallet } from "../../../config/styles";
import { Dev } from "../../../config/util/consoleUtil";

import MainBorderButton from "../../../components/MainBorderButton";
import MainSolidButton from "../../../components/MainSolidButton";
import MainText from "../../../components/MainText";
import PageHeader from "../../../components/PageHeader";

import { PostStatusType } from "../../../types/database";

export default function ReportPage() {
  const navigate = useNavigate();
  const params = useParams();

  const { pushAlert } = useModal();

  const ReportOne = ReportService.One();
  const ReportDelete = ReportService.Delete();
  const ReportUpdate = ReportService.Update();

  const UserOne = AuthService.One();
  const SpottOne = SpottService.One();

  const [loadingStatus, setLoadingStatus] = React.useState<PostStatusType | null>(null);

  const getUserInfo = async () => {
    try {
      await UserOne.runRequest(String(ReportOne.item?.userId));
    } catch (error) {
      console.log(...new Dev("getUserInfo", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao obter informações do usuário",
      });
    }
  };

  const getPostInfo = async () => {
    try {
      await SpottOne.runRequest(String(ReportOne.item?.reportInfo.postId));
    } catch (error) {
      console.log(...new Dev("getPostInfo", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao obter informações da postagem",
      });
    }
  };

  const handleDelete = async () => {
    try {
      pushAlert({
        isConfirm: true,
        type: "delete",
        onConfirm: async () => {
          await ReportDelete.runRequest(params?.reportId || "");

          pushAlert({
            type: "success",
            message: "Denúncia removida",
          });

          navigate(-1);
        },
        message: "Você deseja realmente remover essa denúncia?",
      });
    } catch (error) {
      console.log(...new Dev("handleDelete", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao remover denúncia",
      });
    }
  };

  const handleChangeStatus = async (status: PostStatusType) => {
    try {
      const nowDate = new Date();

      const payload: any = { status };

      if (status === "approved") {
        setLoadingStatus("approved");
        payload.approvedAtTimestamp = nowDate.getTime();
        payload.approvedAtISO = nowDate.toISOString();
      } else {
        setLoadingStatus("rejected");
        payload.rejectedAtTimestamp = nowDate.getTime();
        payload.rejectedAtISO = nowDate.toISOString();
      }

      await ReportUpdate.runRequest(String(params.reportId), payload);

      if (params.reportId) ReportOne.runRequest(params.reportId);

      pushAlert({
        type: "success",
        message: "Status alterado",
      });
    } catch (error) {
      console.log(...new Dev("handleChangeStatus", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao atualizar denúncia",
      });
    } finally {
      setLoadingStatus(null);
    }
  };

  React.useEffect(() => {
    if (params.reportId)
      ReportOne.runRequest(params.reportId).catch((err) =>
        pushAlert({
          type: "error",
          message: "Erro ao obter reports. " + (err as Error).message,
        })
      );
    else navigate(-1);
  }, []);

  React.useEffect(() => {
    if (ReportOne.item) {
      getPostInfo();
      getUserInfo();
    }
  }, [ReportOne.item]);

  return (
    <Box
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
      pt={"30px"}
      pb={"45px"}
    >
      <Box width={"90%"}>
        <PageHeader
          leftIcon={<ArrowBackIosNewRoundedIcon />}
          onLeftAction={() => navigate(-1)}
          title="Denúncia"
        />

        {ReportOne.isLoading ? (
          <Box>
            <Skeleton
              animation="wave"
              variant="rounded"
              width={"100%"}
              height={194}
              sx={{ borderRadius: "15px", margin: "0px 4px", mb: 3 }}
            />
            <Skeleton
              animation="wave"
              variant="rounded"
              width={"100%"}
              height={75}
              sx={{ borderRadius: "15px", margin: "0px 4px", mb: 3 }}
            />
            <Skeleton
              animation="wave"
              variant="rounded"
              width={"100%"}
              height={40}
              sx={{ borderRadius: "15px", margin: "0px 4px", mb: 3 }}
            />
            <Skeleton
              animation="wave"
              variant="rounded"
              width={"100%"}
              height={94}
              sx={{ borderRadius: "15px", margin: "0px 4px", mb: 3 }}
            />
            <Skeleton
              animation="wave"
              variant="rounded"
              width={"100%"}
              height={60}
              sx={{ borderRadius: "15px", margin: "0px 4px", mb: 3 }}
            />
          </Box>
        ) : ReportOne.item ? (
          <>
            {SpottOne.isLoading ? (
              <Skeleton
                animation="wave"
                variant="rounded"
                width={"100%"}
                height={98}
                sx={{ borderRadius: "15px", margin: "0px 4px", mb: 3 }}
              />
            ) : SpottOne.item ? (
              <ButtonBase
                sx={{ mt: 2 }}
                onClick={() => navigate("/post/" + SpottOne.item?.id)}
                style={{
                  border: `3px solid ${GreyPallet.Light}`,
                  borderRadius: 15,
                  width: "100%",
                  overflow: "hidden",
                  marginBottom: 20,
                }}
              >
                <Box
                  style={{
                    textAlign: "start",
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  px={2}
                  py={1}
                >
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <MainText color="black" size="caption">
                      {SpottOne.item?.text || "-"}
                    </MainText>

                    <MainText color="regular" size="small">
                      {DateTime.fromMillis(SpottOne?.item?.createdAtTimestamp || 0).toFormat(
                        "dd/LL/y HH:mm"
                      )}
                    </MainText>

                    {ReportOne?.item?.reportInfo?.commentId ? (
                      SpottOne?.item?.comments.some(
                        (comment) => comment.id === ReportOne?.item?.reportInfo?.commentId
                      ) ? (
                        SpottOne?.item?.comments
                          .filter(
                            (comment) => comment.id === ReportOne?.item?.reportInfo?.commentId
                          )
                          .map((comment) => (
                            <Box
                              key={comment.id}
                              style={{ border: `3px solid ${GreyPallet.Light}`, borderRadius: 15 }}
                              px={2}
                              py={1}
                              mt={1}
                            >
                              <MainText color="black" size="caption">
                                {comment.text || "-"}
                              </MainText>
                              <MainText color="regular" size="small">
                                {DateTime.fromMillis(
                                  SpottOne?.item?.createdAtTimestamp || 0
                                ).toFormat("dd/LL/y HH:mm")}
                              </MainText>
                            </Box>
                          ))
                      ) : (
                        <Box sx={{ mt: 1 }}>
                          <MainText size="small" color="regular">
                            O comentário denunciado não existe...
                          </MainText>
                        </Box>
                      )
                    ) : null}
                  </Box>
                </Box>
              </ButtonBase>
            ) : (
              <Box sx={{ mt: 1 }}>
                <MainText size="small" color="regular">
                  A postagem denunciada não existe...
                </MainText>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <MainText color="regular">Denúncia:</MainText>

            <Box my={2}>
              <MainText>{ReportOne.item.text}</MainText>{" "}
            </Box>

            <Box my={2}>
              {UserOne.isLoading ? (
                <Skeleton
                  animation="wave"
                  variant="rounded"
                  width={"100%"}
                  height={98}
                  sx={{ borderRadius: "15px", margin: "0px 4px", mb: 3 }}
                />
              ) : ReportOne.item ? (
                <ButtonBase
                  onClick={() => navigate("/user/" + ReportOne.item?.userId)}
                  style={{
                    border: `3px solid ${GreyPallet.Light}`,
                    borderRadius: 15,
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    style={{
                      textAlign: "start",
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                    px={2}
                    py={1}
                  >
                    <Box
                      style={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <MainText color="black" size="caption">
                        {UserOne?.item?.name || "Usuário sem nome"}
                      </MainText>
                      <MainText color="regular" size="caption">
                        {UserOne?.item?.email || "-"}
                      </MainText>
                    </Box>

                    <ArrowForwardIosIcon style={{ color: GreyPallet.Regular }} />
                  </Box>
                </ButtonBase>
              ) : null}
            </Box>

            <Box my={2}>
              <MainText color="regular" size="caption">
                {DateTime.fromMillis(ReportOne.item.createdAtTimestamp).toFormat("dd/LL/y HH:mm")}
              </MainText>
            </Box>

            {/* <Box style={{ display: "flex", justifyContent: "center", gap: 15 }} mb={5}>
              <MainSolidButton
                color="grey"
                text={String(ReportOne.item?.likes.length)}
                icon={<FavoriteIcon />}
              />
              <MainSolidButton
                color="grey"
                text={String(ReportOne.item?.comments.length)}
                icon={<ChatBubbleIcon />}
              />
            </Box> */}

            <MainBorderButton
              color="danger"
              text="Deletar"
              loading={ReportDelete.isLoading}
              onClick={handleDelete}
            />

            <Box my={2} display={"flex"} gap={1} alignItems={"center"}>
              {ReportOne.item.status === "approved" ? (
                <CheckCircleRounded color="success" />
              ) : ReportOne.item.status === "rejected" ? (
                <CancelRounded color="error" />
              ) : (
                <PendingActionsRounded />
              )}
              <MainText color="regular" size="caption">
                {ReportOne.item.status === "approved"
                  ? "Denúncia aprovada"
                  : ReportOne.item.status === "rejected"
                  ? "Denúncia rejeitada"
                  : "Denúncia aguardando avaliação"}
              </MainText>
            </Box>

            {ReportOne.item?.status !== "rejected" && (
              <Box mt={2}>
                <MainBorderButton
                  color="black"
                  text="Rejeitar"
                  loading={loadingStatus === "rejected"}
                  onClick={() => handleChangeStatus("rejected")}
                />
              </Box>
            )}

            {ReportOne.item?.status !== "approved" && (
              <Box mt={2}>
                <MainSolidButton
                  color="black"
                  text="Aprovar"
                  loading={loadingStatus === "approved"}
                  onClick={() => handleChangeStatus("approved")}
                />
              </Box>
            )}
          </>
        ) : (
          <MainText size="caption" color="regular">
            Nenhuma postagem encontrada...
          </MainText>
        )}
      </Box>
    </Box>
  );
}
